const A11Y_TAB_LEVEL = {
    ONE: 'ah-tablevel-1',
    TWO: 'ah-tablevel-2',
    THREE: 'ah-tablevel-3'
};

/**
 * Class to handle the a11y-compliant behavior for tabs. A11y-compliant tabs require both properly-formatted HTML markup as well as Javascript to
 * implement the proper keyboarding behavior. See this Confluence page for Tabs documentation: https://healthedge.atlassian.net/l/cp/LGLc1QtC
 *
 * @param {string} tabListId ID of the div containing the tablist (i.e. role="tablist")
 * @param {string} tabPanelsId ID of the div containing the tabpanels (i.e. parent container of the role="tabpanel" DIVs)
 * @param {string} [tabLevel=1] optional numeric indicator for the level of the tablist (used for nested tabs)
 * @param {string} [parentTabListId=null] required for 2nd- or 3rd-level tablist elements; ID of the PARENT tablist div (e.g. the 1st-level DIV, or 2nd-level DIV if this is a 3rd-level tablist)
 */
let A11yTabs = class A11yTabsClass {

    #tabListId;
    #tabPanelsId;
    #tabLevel;
    #parentTabListId;
    #tablist;
    #tabs;
    #tabpanels;
    #parentTabList;

    constructor(tabListId, tabPanelsId, tabLevel, parentTabListId) {
        this.#tabListId = tabListId;
        this.#tabPanelsId = tabPanelsId;
        this.#tabLevel = tabLevel ? tabLevel : A11Y_TAB_LEVEL.ONE;
        this.#parentTabListId = parentTabListId;

        let _this = this;

        if ((tabLevel === A11Y_TAB_LEVEL.TWO || tabLevel === A11Y_TAB_LEVEL.THREE) && !parentTabListId) {
            DevUtils.logError('No parentTabListId passed in for a Level 2 or Level 3 tablist', 'A11yTabs.constructor()');
            return;
        }

        _this.#initTabs();
    }

    selectTabByIndex(index, isMouseClick) {
        this.#selectTab(index, isMouseClick);
    }

    /////////////////////
    // private methods //
    /////////////////////
    #initTabs() {
        let _this = this;

        this.#tablist = document.querySelector('#' + this.#tabListId + '.ah-tablist');
        this.#tabs = document.querySelectorAll('#' + this.#tabListId + ' .ah-tab:not([disabled="true"])');
        this.#tabpanels = document.querySelectorAll('#' + this.#tabPanelsId + ' .ah-tabpanel');
        let activeTab = document.querySelector('#' + this.#tabListId + ' .ah-tab[aria-selected="true"]');

        if (!this.#tablist) {
            DevUtils.logWarn('Cannot initialize tabs, tablist is null for [' + this.#tabListId + ']', 'A11yTabs.initTabs()');
            return;
        }

        if (this.#parentTabListId) {
            this.#parentTabList = document.querySelector('#' + this.#parentTabListId + '.ah-tablist');
        }

        this.#logDebug('initTabs()', { tablist: this.#tablist, tabs: this.#tabs, tabpanels: this.#tabpanels, parentTabList: this.#parentTabList });

        // set the tab level on the tablist (purely for styling only)
        this.#tablist.classList.add(this.#tabLevel);

        // select the previous/next tabs using left and right arrow keys
        this.#tablist.addEventListener('keydown', function (e) {
            _this.#triggerKeydownHandler(e);
        });

        // activate the tabs when they are clicked (also handles Enter and Space keys)
        this.#tabs.forEach(function (tab) {
            // do this in case A11yModal.trapFocusInModal() has inadvertantly set tabindex to 0 on inactive tabs
            if (tab !== activeTab) {
                tab.setAttribute('tabindex', -1);
            }

            tab.addEventListener('click', function (e) {
                _this.#triggerClickHandler(e);
            });
        });

        this.#adjustTabFontSize();
    }

    #triggerKeydownHandler(e) {
        switch (e.key) {
            case 'ArrowLeft':
                this.#selectPreviousTab();
                break;
            case 'ArrowRight':
                this.#selectNextTab();
                break;
            case 'Home':
                e.preventDefault();  // prevent page scroll
                this.#selectFirstTab();
                break;
            case 'End':  // optional
                e.preventDefault();  // prevent page scroll
                this.#selectLastTab();
                break;
        }
    }

    #triggerClickHandler(e) {
        let nextTabIndex = Array.prototype.slice.call(this.#tabs).indexOf(e.target);
        this.#selectTab(nextTabIndex, true);
    }

    /**
     * Tab selection methods
     */
    #selectFirstTab() {
        this.#selectTab(0);
    }

    #selectLastTab() {
        this.#selectTab(this.#tabs.length - 1);
    }

    #selectNextTab() {
        let activeTab = document.querySelector('#' + this.#tabListId + ' .ah-tab[aria-selected="true"]');
        let activeTabIndex = Array.prototype.slice.call(this.#tabs).indexOf(activeTab);

        // Select the next tab, wrapping to the first tab if needed
        if (activeTabIndex < this.#tabs.length - 1) {
            this.#selectTab(activeTabIndex + 1);
        } else {
            this.#selectTab(0);
        }
    }

    #selectPreviousTab() {
        let activeTab = document.querySelector('#' + this.#tabListId + ' .ah-tab[aria-selected="true"]');
        let activeTabIndex = Array.prototype.slice.call(this.#tabs).indexOf(activeTab);

        // Select the previous tab, wrapping to last tab if needed
        if (activeTabIndex > 0) {
            this.#selectTab(activeTabIndex - 1);
        } else {
            this.#selectTab(this.#tabs.length - 1);
        }
    }

    #selectTab(index, isMouseClick) {
        // Deactivate all tabs
        this.#tabs.forEach(function (tab) {
            tab.setAttribute('aria-selected', false);
            tab.setAttribute('tabindex', -1);
        });

        // Activate only the requested tab, and set focus to it
        this.#tabs[index].setAttribute('aria-selected', true);
        this.#tabs[index].setAttribute('tabindex', 0);
        this.#tabs[index].focus();

        if (!isMouseClick) {
            this.#tabs[index].click();  // do this in case there is an onclick defined on the tab button
        }

        // Hide all tabpanels
        this.#tabpanels.forEach(function (tabpanel) {
            tabpanel.classList.remove('is-visible');
        });

        // Show only the tabpanel for the requested tab
        let nextTabpanel = document.querySelector('#' + this.#tabs[index].getAttribute('aria-controls'));
        if (nextTabpanel) {
            nextTabpanel.classList.add('is-visible');
        } else {
            DevUtils.logWarn('Cannot find nextTabPanel to make visible for tab [' + this.#tabs[index].getAttribute('id') + '] in tablist [' + this.#tabListId + ']. Check to make sure aria-controls is set correctly on the ah-tab element', 'A11yTabs.selectTab()');
        }

        this.#adjustTabFontSize();
    }

    #adjustTabFontSize() {
        if (this.#tabLevel === A11Y_TAB_LEVEL.THREE) {
            $(this.#parentTabList).addClass('parent');  // change font size of 2nd level to smaller
        } else if (this.#tabLevel === A11Y_TAB_LEVEL.TWO) {
            $(this.#parentTabList).addClass('parent');  // change font size of 1st level to smaller
            $(this.#tablist).removeClass('parent');     // change font size of this level to normal
        } else if (this.#tabLevel === A11Y_TAB_LEVEL.ONE) {
            $(this.#tablist).addClass('parent');    // level one should always be smaller font
        }
    }

    #logDebug(message, object) {
        DevUtils.logDebug(message, 'A11yTabs', object);
    }
};
